<template>
  <div class="o-main-content-area report-page">
    <div class="report-page__header">
      <h4 class="report-page__title">Onboarding Profiles</h4>
      <p class="report-page__sub">
        Presets that drive the worker-onboarding flow — skill defaults,
        link TTLs, immediate-assign capability, completion notifications.
      </p>
      <div class="report-page__controls" style="display:flex; gap:12px; align-items:center;">
        <button class="btn btn-primary" @click="onNew">+ New profile</button>
        <span v-if="loading" class="report-loading">Loading…</span>
        <span v-if="error" class="report-error">{{ error }}</span>
      </div>
    </div>

    <table class="onboarding-profiles-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Slug</th>
          <th>Default?</th>
          <th>Active?</th>
          <th>SMS link TTL</th>
          <th>Immediate-assign</th>
          <th>Photo</th>
          <th>Onboarding link</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="row.id">
          <td>{{ row.profile_name }}</td>
          <td><code>{{ row.slug }}</code></td>
          <td>{{ row.is_default ? '✓' : '' }}</td>
          <td>{{ row.active ? '✓' : '—' }}</td>
          <td>{{ ttlSummary(row) }}</td>
          <td>{{ row.allows_immediate_assign ? '✓' : '—' }}</td>
          <td>{{ row.photo_mode }}</td>
          <!-- #1798 — the link and QR live HERE, on the row, because this is the
               inventory a rep opens to hand a link out. The endpoints have
               existed since June; nothing on this page ever called them, which
               is why cs3me_short_url is NULL on every profile ever created. -->
          <td class="op-link-cell">
            <a v-if="row.cs3me_url" :href="row.cs3me_url" target="_blank" rel="noopener"
               class="op-link" :data-testid="'op-url-' + row.id">{{ row.cs3me_url }}</a>
            <em v-else class="op-link-none">not generated yet</em>
            <div class="op-link-actions">
              <button class="btn btn-sm" :disabled="linkBusy === row.id"
                      :data-testid="'op-link-' + row.id"
                      @click="onRefreshLink(row)"
                      title="Generate (or regenerate) the short onboarding link for this profile.">
                <i class="fa" :class="linkBusy === row.id ? 'fa-spinner fa-spin' : 'fa-link'" aria-hidden="true"></i>
                {{ row.cs3me_url ? 'Regenerate' : 'Get link' }}
              </button>
              <button class="btn btn-sm" :disabled="qrBusy === row.id"
                      :data-testid="'op-qr-' + row.id"
                      @click="onShowQr(row)" title="Show a QR code for this profile's link.">
                <i class="fa" :class="qrBusy === row.id ? 'fa-spinner fa-spin' : 'fa-qrcode'" aria-hidden="true"></i>
                QR
              </button>
              <button v-if="row.cs3me_url" class="btn btn-sm"
                      :data-testid="'op-copy-' + row.id"
                      @click="onCopyLink(row)" title="Copy the link to the clipboard.">
                <i class="fa fa-clipboard" aria-hidden="true"></i>
              </button>
            </div>
            <span v-if="rowNote[row.id]" class="op-link-note">{{ rowNote[row.id] }}</span>
          </td>
          <td>
            <button class="btn btn-sm" @click="onEdit(row)" title="Edit profile" aria-label="Edit profile"><i class="fa fa-pencil-square-o" aria-hidden="true"></i></button>
            <button class="btn btn-sm btn-danger" v-if="!row.is_default"
                    @click="onDelete(row)">Delete</button>
          </td>
        </tr>
        <tr v-if="!rows.length && !loading">
          <td colspan="8" style="padding:18px;text-align:center;opacity:.65;">
            No profiles yet — click <em>+ New profile</em> to create one.
          </td>
        </tr>
      </tbody>
    </table>

    <!-- #1798 QR. A data URI from the server, shown inline — nothing to
         download and no second round trip to look at it. -->
    <div v-if="qrFor" class="op-qr-modal" data-testid="op-qr-modal" @click.self="qrFor = null">
      <div class="op-qr-modal__inner">
        <h4>{{ qrFor.name }}</h4>
        <img v-if="qrFor.url" :src="qrFor.url" alt="Onboarding QR code" class="op-qr-img" />
        <p class="op-qr-link"><a :href="qrFor.link" target="_blank" rel="noopener">{{ qrFor.link }}</a></p>
        <button class="btn btn-sm" data-testid="op-qr-close" @click="qrFor = null">Close</button>
      </div>
    </div>

    <!-- Edit / new drawer -->
    <div v-if="drawerOpen" class="profile-drawer">
      <div class="profile-drawer__inner">
        <h4>{{ drawerEditing && drawerEditing.id ? 'Edit profile' : 'New profile' }}</h4>
        <p v-if="formError" class="form-error-banner">{{ formError }}</p>
        <form @submit.prevent="onSave" novalidate>
          <label>Profile name
            <input v-model="form.profile_name" maxlength="120"
                   @blur="autoDeriveSlug">
          </label>
          <label>Slug (URL-safe, auto-derived from name if blank)
            <input v-model="form.slug" maxlength="80"
                   placeholder="auto-generated">
          </label>
          <label>Description (shown under "Welcome" on the worker form)
            <input v-model="form.description" maxlength="500"
                   placeholder="Stagehand overhire onboarding">
          </label>
          <label>Onboarding note (admin instructions; attached as a Note on the new worker)
            <textarea v-model="form.onboarding_note" rows="3" maxlength="4000"
                      placeholder="Welcome to Local 134. Bring boots and gloves to your first call."></textarea>
          </label>
          <label>Skill defaults (comma-separated)
            <input v-model="form.skill_defaults_raw" placeholder="Stagehand, Audio">
          </label>
          <label>Local status
            <input v-model="form.local_status" maxlength="60"
                   placeholder="permit / dep / traveler / member">
          </label>
          <label>SMS link TTL — hours
            <input type="number" min="0" max="168" v-model.number="form.sms_link_ttl_hours">
          </label>
          <label>SMS link TTL — minutes (overrides hours when set, for 15-min reserved slot)
            <input type="number" min="0" max="1440" v-model.number="form.sms_link_ttl_minutes">
          </label>
          <label class="checkbox">
            <input type="checkbox" v-model="form.allows_immediate_assign">
            Allows immediate-assign
          </label>
          <label>Photo mode
            <select v-model="form.photo_mode">
              <option value="off">Off</option>
              <option value="optional">Optional</option>
              <option value="required">Required</option>
            </select>
          </label>
          <label>Photo instructions
            <input v-model="form.photo_instructions" maxlength="500">
          </label>
          <!-- ══ #1890 — the QR gates (#1827), which had no UI at all ══
               Enforced on every public request since #1827, and until now
               changeable only by a hand-written UPDATE on production.
               The markup is SHARED with Config -> Onboarding, which is the
               screen the product actually navigates to. -->
          <qr-gates
            :profile="form"
            :issued-today="drawerEditing && drawerEditing.qr_issued_today"
            :show-usage="!!drawerEditing"
          />

          <label class="checkbox">
            <input type="checkbox" v-model="form.is_default">
            Default for this client
          </label>
          <label class="checkbox">
            <input type="checkbox" v-model="form.active">
            Active
          </label>

          <div class="profile-drawer__buttons">
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{ saving ? 'Saving…' : 'Save' }}
            </button>
            <button type="button" class="btn" @click="onCancel">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import service from '../../services.js';
import { qrFormFrom, qrPayload, blockingWarnings } from '../../qrGateRules';
// #1890 — the gates themselves are ONE component, shared with Config ->
// Onboarding (`components/configuration/configOnboarding.vue`), which is the
// screen the product navigates to. This one is reachable only by its own route.
import QrGates from './qrGates.vue';

const blankForm = () => ({
  profile_name: '',
  slug: '',
  description: '',
  onboarding_note: '',
  skill_defaults_raw: '',
  local_status: 'permit',
  sms_link_ttl_hours: 48,
  sms_link_ttl_minutes: null,
  allows_immediate_assign: false,
  photo_mode: 'optional',
  photo_instructions: 'Headshot, eyes-open, no hat.',
  is_default: false,
  active: true,
  // #1890 — off, capped and SMS-verified out of the box. A new profile must
  // never arrive carrying the least safe setting.
  ...qrFormFrom(null),
});

export default {
  name: 'OnboardingProfiles',
  components: { QrGates },
  data() {
    return {
      // #1798 — per-row link/QR state, keyed by row id so two rows cannot
      // share a spinner or a message.
      linkBusy: null,
      qrBusy: null,
      qrFor: null,
      rowNote: {},
      rows: [],
      loading: false,
      error: null,
      drawerOpen: false,
      drawerEditing: null,
      form: blankForm(),
      saving: false,
      formError: null,
    };
  },
  mounted() { this.fetch(); },
  methods: {
    ttlSummary(row) {
      if (row.sms_link_ttl_minutes && row.sms_link_ttl_minutes > 0) {
        return row.sms_link_ttl_minutes + ' min';
      }
      return (row.sms_link_ttl_hours ?? 48) + ' hr';
    },
    async fetch() {
      this.loading = true; this.error = null;
      try {
        const res = await service.getUrl('/api/v3/onboarding/profiles');
        this.rows = (res?.data?.data ?? []);
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Failed to load';
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },
    /**
     * #1798 — generate (or regenerate) the short onboarding link.
     *
     * THE ENDPOINT HAS EXISTED SINCE JUNE AND NOTHING ON THIS PAGE EVER CALLED
     * IT. That is why `cs3me_short_url` is NULL on every profile ever created,
     * including the June one — not because rows were unreachable, but because
     * the inventory never had the control.
     *
     * The response's `cs3me_url` is authoritative and is written straight back
     * onto the row, so the table reflects the server rather than a guess about
     * what it did.
     */
    async onRefreshLink(row) {
      if (this.linkBusy) return;
      this.linkBusy = row.id;
      this.$set(this.rowNote, row.id, '');
      try {
        const res = await service.postUrl(
          '/api/v3/onboarding/profiles/' + row.id + '/link/refresh', {});
        const url = res?.data?.data?.cs3me_url || null;
        this.$set(row, 'cs3me_url', url);
        if (!url) {
          this.$set(this.rowNote, row.id, 'No link was returned. The profile has no short code yet.');
          return;
        }
        // SAY WHICH KIND OF LINK THIS IS. mintCs3meShortUrl() returns null when
        // YOURLS is down or has no signature configured, and cs3meUrlFor() then
        // falls back to an interim URL. Both work; only one is a cs3.me short
        // link, and a rep handing out the long one should know that is what they
        // have rather than wonder why it is not short.
        this.$set(this.rowNote, row.id,
          url.indexOf('cs3.me') === -1
            ? 'Interim link — cs3.me short-linking is not configured on this environment.'
            : '');
      } catch (e) {
        this.$set(this.rowNote, row.id, this.actionError(e, 'The link could not be generated.'));
      } finally {
        this.linkBusy = null;
      }
    },

    /** #1798 — a QR for the profile's link. Stateless server-side; no DB write. */
    async onShowQr(row) {
      if (this.qrBusy) return;
      this.qrBusy = row.id;
      this.$set(this.rowNote, row.id, '');
      try {
        const res = await service.postUrl(
          '/api/v3/onboarding/profiles/' + row.id + '/qr/refresh', {});
        const qr = res?.data?.data?.qr_url || null;
        if (!qr) {
          this.$set(this.rowNote, row.id, 'No QR was returned for this profile.');
          return;
        }
        this.qrFor = { name: row.profile_name, url: qr, link: row.cs3me_url || '' };
      } catch (e) {
        // 422 no_short_code is the one worth naming: it is a real state (the
        // profile has no code) and not a failure of the QR itself.
        const code = e?.response?.data?.error;
        this.$set(this.rowNote, row.id, code === 'no_short_code'
          ? 'This profile has no short code yet — press Get link first.'
          : this.actionError(e, 'The QR code could not be generated.'));
      } finally {
        this.qrBusy = null;
      }
    },

    async onCopyLink(row) {
      const url = row.cs3me_url;
      if (!url) return;
      try {
        await navigator.clipboard.writeText(url);
        this.$set(this.rowNote, row.id, 'Link copied.');
      } catch (e) {
        // Clipboard is blocked on insecure origins and in some browsers. Say so
        // rather than reporting a copy that did not happen.
        this.$set(this.rowNote, row.id, 'Could not copy — select the link and copy it manually.');
      }
    },

    /** One place that turns an axios failure into something a rep can act on. */
    actionError(e, fallback) {
      const status = e?.response?.status;
      const body = e?.response?.data || {};
      if (status === 403) {
        return 'Blocked (403): the tenant could not be resolved from this hostname.';
      }
      if (status) {
        return fallback + ' (HTTP ' + status + (body.error ? ': ' + body.error : '') + ')';
      }
      return fallback + ' ' + (e?.message || '');
    },


    onNew() {
      this.drawerEditing = null;
      this.form = blankForm();
      this.drawerOpen = true;
      this.formError = null;
    },
    onEdit(row) {
      this.drawerEditing = row;
      this.form = {
        profile_name:            row.profile_name,
        slug:                    row.slug,
        description:             row.description || '',
        onboarding_note:         row.onboarding_note || '',
        skill_defaults_raw:      Array.isArray(row.skill_defaults) ? row.skill_defaults.join(', ') : '',
        local_status:            row.local_status,
        sms_link_ttl_hours:      row.sms_link_ttl_hours,
        sms_link_ttl_minutes:    row.sms_link_ttl_minutes,
        allows_immediate_assign: !!row.allows_immediate_assign,
        photo_mode:              row.photo_mode || 'optional',
        photo_instructions:      row.photo_instructions || '',
        is_default:              !!row.is_default,
        active:                  !!row.active,
        ...qrFormFrom(row),   // #1890
      };
      this.drawerOpen = true;
      this.formError = null;
    },
    onCancel() { this.drawerOpen = false; },
    slugify(s) {
      return (s || '')
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-+|-+$)/g, '')
        .slice(0, 80);
    },
    autoDeriveSlug() {
      if (!this.form.slug && this.form.profile_name) {
        this.form.slug = this.slugify(this.form.profile_name);
      }
    },
    async onSave() {
      this.formError = null;

      const name = (this.form.profile_name || '').trim();
      if (!name) {
        this.formError = 'Profile name is required.';
        return;
      }
      const slug = this.slugify(this.form.slug || this.form.profile_name);
      if (!slug) {
        this.formError = 'Could not derive a slug from the profile name. Pick something with letters or numbers.';
        return;
      }
      const localStatus = (this.form.local_status || '').trim() || 'permit';
      const skills = (this.form.skill_defaults_raw || '')
        .split(',').map(s => s.trim()).filter(Boolean);
      if (skills.length === 0) {
        this.formError = 'Add at least one skill (comma-separated).';
        return;
      }

      // ⚠ #1890 — the settings that fail CLOSED and silently are confirmed
      // before they are saved. A cap of 0 blocks every submission; no expiry
      // makes the code permanent; SMS off is the only rail that costs an
      // automated scan anything. In each case the worker just sees a refusal, so
      // nobody finds out until a rep is standing next to a poster that does not
      // work.
      const blocking = blockingWarnings(this.form);
      if (blocking.length) {
        try {
          await this.$confirm(
            blocking.map((w) => '• ' + w.text).join('\n\n'),
            'Save these QR settings?',
            { confirmButtonText: 'Save anyway', cancelButtonText: 'Go back', type: 'warning' }
          );
        } catch (e) {
          return;   // "Go back" — nothing saved
        }
      }

      this.saving = true;
      try {
        const payload = {
          profile_name:            name,
          slug:                    slug,
          description:             this.form.description || null,
          onboarding_note:         this.form.onboarding_note || null,
          skill_defaults:          skills,
          local_status:            localStatus,
          sms_link_ttl_hours:      this.form.sms_link_ttl_hours ?? 48,
          sms_link_ttl_minutes:    this.form.sms_link_ttl_minutes || null,
          allows_immediate_assign: !!this.form.allows_immediate_assign,
          photo_mode:              this.form.photo_mode,
          photo_instructions:      this.form.photo_instructions || null,
          is_default:              !!this.form.is_default,
          active:                  !!this.form.active,
          ...qrPayload(this.form),   // #1890
        };
        // Reflect normalized slug back to the form so the user sees it.
        this.form.slug = slug;

        let createdId = null;
        if (this.drawerEditing && this.drawerEditing.id) {
          await service.putUrl('/api/v3/onboarding/profiles/' + this.drawerEditing.id, payload);
        } else {
          const created = await service.postUrl('/api/v3/onboarding/profiles', payload);
          createdId = created?.data?.data?.id || null;
        }
        this.drawerOpen = false;
        await this.fetch();

        // #1798 — ed reported a profile that saves and then is not in the list.
        // The re-fetch above has always been here and the list endpoint applies
        // no filters, so this should never fire — which is exactly why it is
        // worth stating if it does. A create that succeeds and then does not
        // appear is otherwise indistinguishable from a create that failed, and
        // the rep is left guessing which.
        if (createdId && !this.rows.some((r) => r.id === createdId)) {
          this.error = 'The profile was created but did not come back in the list. ' +
            'Reload the page; if it is still missing, the list and the create are ' +
            'resolving different tenants.';
          if (window.console && console.warn) {
            console.warn('[#1798] created profile ' + createdId + ' absent from the refreshed list', this.rows);
          }
        }
      } catch (e) {
        const status = e?.response?.status;
        const errs = e?.response?.data?.errors;
        if (errs) {
          this.formError = 'Validation: ' + Object.values(errs).flat().join('; ');
        } else if (status === 403) {
          this.formError = 'Save blocked (403): tenant could not be resolved from this hostname. ' +
            'You may be on the wrong subdomain — try the tenant URL (e.g. iatse2.…) instead of admin.…';
        } else if (status) {
          this.formError = 'Save failed (HTTP ' + status + '): ' +
            (e?.response?.data?.error || JSON.stringify(e?.response?.data || {}));
        } else {
          this.formError = 'Network error: ' + (e?.message || 'unknown');
        }
      } finally {
        this.saving = false;
      }
    },
    async onDelete(row) {
      if (!confirm('Delete onboarding profile "' + row.profile_name + '"?')) return;
      try {
        await service.delUrl('/api/v3/onboarding/profiles/' + row.id);
        await this.fetch();
      } catch (e) {
        alert(e?.response?.data?.error || 'Delete failed');
      }
    },
  },
};
</script>

<style scoped>
.report-page__sub {
  font-size: 13px; color: #4a5568; margin: 4px 0 12px;
}
/* #1798 — the link and QR live on the inventory row, because that is the page a
   rep opens to hand a link out. */
.op-link-cell { min-width: 260px; }
.op-link { word-break: break-all; font-size: 12px; }
.op-link-none { opacity: .6; font-size: 12px; }
.op-link-actions { margin-top: 4px; display: flex; gap: 4px; flex-wrap: wrap; }
.op-link-note { display: block; margin-top: 4px; font-size: 11px; color: #7a5b00; }
.op-qr-modal {
  position: fixed; inset: 0; background: rgba(0,0,0,.45);
  display: flex; align-items: center; justify-content: center; z-index: 1050;
}
.op-qr-modal__inner {
  background: #fff; border-radius: 6px; padding: 16px 20px;
  text-align: center; max-width: 90vw;
}
.op-qr-img { width: 260px; height: 260px; max-width: 70vw; max-height: 70vw; }
.op-qr-link { font-size: 12px; word-break: break-all; margin: 8px 0; }

.onboarding-profiles-table {
  width: 100%; border-collapse: collapse; background: #fff;
  border: 1px solid #e2e8f0; font-size: 14px;
}
.onboarding-profiles-table th,
.onboarding-profiles-table td {
  padding: 8px 12px; text-align: left; border-bottom: 1px solid #e2e8f0;
}
.onboarding-profiles-table th {
  background: #f7fafc; font-weight: 600; color: #4a5568; font-size: 13px;
}
.onboarding-profiles-table tr:last-child td { border-bottom: none; }
.profile-drawer {
  position: fixed; top: 0; right: 0; bottom: 0; width: 480px;
  background: #fff; box-shadow: -4px 0 24px rgba(0,0,0,.08);
  padding: 24px; overflow-y: auto; z-index: 1000;
}
.profile-drawer__inner h4 { margin-top: 0; }
.form-error-banner {
  background: #fff5f5; border: 1px solid #fc8181; color: #9b2c2c;
  padding: 10px 14px; border-radius: 4px; margin: 0 0 12px;
  font-size: 13px; line-height: 1.4;
}
.profile-drawer label { display: block; margin-bottom: 12px; font-size: 13px; color: #4a5568; }
.profile-drawer label.checkbox { display: flex; align-items: center; gap: 6px; }
.profile-drawer input[type=text],
.profile-drawer input:not([type]),
.profile-drawer input[type=number],
.profile-drawer select {
  display: block; width: 100%; padding: 6px 8px; margin-top: 3px;
  border: 1px solid #cbd5e0; border-radius: 3px; font-size: 14px;
}
.profile-drawer__buttons { margin-top: 16px; display: flex; gap: 8px; }
.btn { padding: 6px 14px; border: 1px solid #cbd5e0; background: #fff; border-radius: 3px; cursor: pointer; }
.btn-primary { background: #3182ce; color: #fff; border-color: #3182ce; }
.btn-primary:disabled { opacity: .55; }
.btn-sm { padding: 3px 9px; font-size: 12px; margin-right: 4px; }
.btn-danger { color: #c53030; }
.report-loading { color: #4a5568; }
.report-error { color: #c53030; }
.qr-gates { border: 1px solid #ddd; border-radius: 4px; padding: 8px 12px; margin: 10px 0; }
.qr-gates legend { font-size: 12px; font-weight: 600; padding: 0 4px; }
.qr-gates__lede { font-size: 12px; color: #777; margin: 2px 0 8px; }
.qr-gates__usage { font-size: 12px; color: #555; margin: 6px 0 0; }
.qr-gates__warnings { margin: 8px 0 0; padding-left: 18px; font-size: 12px; color: #a33; }
</style>
